import { useRef, useState } from "react";

// Emojis compatibles con WhatsApp (Unicode 6.0-8.0, año 2010-2016).
// Se garantiza que se renderizan correctamente en todas las versiones de WhatsApp,
// tanto en escritorio como en móvil viejo.
export const WA_EMOJIS = {
  "Caras": [
    "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🙂", "😉", "😊",
    "☺️", "😍", "😘", "😗", "😙", "😚", "😋", "😜", "😝", "😛",
    "😐", "😑", "😶", "🙄", "😏", "😣", "😥", "😮", "😯", "😪",
    "😫", "😴", "😌", "😲", "☹️", "🙁", "😖", "😞", "😟", "😤",
    "😢", "😭", "😦", "😧", "😨", "😩", "😬", "😰", "😱", "😳",
    "😵", "😡", "😠", "😷", "🤒", "🤕", "😇", "🤠", "🤡",
  ],
  "Manos y gestos": [
    "👍", "👎", "👌", "✌️", "👋", "🙌", "👏", "🙏",
    "💪", "👐", "✊", "👊", "☝️", "👆", "👇", "👉", "👈",
    "🖐️", "✋", "🖖",
  ],
  "Corazones y símbolos": [
    "❤️", "💛", "💚", "💙", "💜", "🖤", "💔",
    "❣️", "💕", "💞", "💓", "💗", "💖", "💘", "💝", "💯", "✨",
    "⭐", "🌟", "💫", "⚡", "🔥", "🎉", "🎊", "🎁", "✅", "❌",
    "❓", "❗", "‼️", "⁉️", "☑️",
  ],
  "Comida (carne)": [
    "🍖", "🍗", "🌭", "🍔", "🍟", "🍕", "🌮", "🌯",
    "🍜", "🍝", "🍱", "🍲", "🍛", "🍚", "🌽", "🍅",
    "🍞", "🧀", "🍳", "🍷", "🍺", "🍻", "🥂",
  ],
  "Locales y transporte": [
    "🏠", "🏡", "🏢", "🏬", "🏪", "🏫", "🏥", "🏦", "🚚", "🚛",
    "🚐", "🚗", "🚙", "🚕", "🏍️", "🚲", "📦", "🛍️",
  ],
  "Objetos útiles": [
    "📞", "☎️", "📱", "📲", "💬", "💭", "📢", "🔔", "🔕", "📍",
    "📌", "📅", "📆", "🕐", "⏰", "⏳", "⌛", "🎯", "📸",
    "📷", "💻", "⌨️", "💰", "💵",
    "💳", "🧾", "📝", "✏️", "📋", "📄", "📁", "📂", "🔒", "🔓",
  ],
};

/**
 * Botón + popover de emojis WhatsApp-safe.
 * `onPick(emoji)` recibe el string del emoji seleccionado.
 * `data-testid` opcional para tests.
 */
export default function WhatsAppEmojiPicker({ onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(Object.keys(WA_EMOJIS)[0]);
  const wrapperRef = useRef(null);

  const handleSelect = (emoji) => {
    onPick?.(emoji);
    // Mantener abierto para permitir varios clicks; el usuario cierra con "Cerrar" o click fuera.
  };

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        data-testid={testId || "wa-emoji-picker-btn"}
        onClick={() => setOpen((v) => !v)}
        title="Insertar emoji"
        className="text-[16px] leading-none px-2 py-1 rounded border border-neutral-300 hover:bg-emerald-50 hover:border-emerald-400 transition"
      >
        😀
      </button>
      {open && (
        <>
          {/* Overlay para cerrar al click afuera */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            data-testid="wa-emoji-picker-overlay"
          />
          <div
            className="absolute z-50 top-full right-0 mt-1 w-[340px] max-h-[360px] bg-white border border-neutral-300 rounded shadow-xl flex flex-col"
            data-testid="wa-emoji-picker-panel"
          >
            {/* Categorías */}
            <div className="flex border-b bg-neutral-50 text-[10px] uppercase tracking-wider overflow-x-auto">
              {Object.keys(WA_EMOJIS).map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCat(cat)}
                  className={`px-2 py-1.5 whitespace-nowrap border-r border-neutral-200 hover:bg-white ${
                    activeCat === cat
                      ? "bg-white text-emerald-700 font-semibold border-b-2 border-b-emerald-500"
                      : "text-neutral-600"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            {/* Grid emojis */}
            <div className="grid grid-cols-8 gap-0.5 p-2 overflow-y-auto flex-1">
              {WA_EMOJIS[activeCat].map((e, i) => (
                <button
                  key={`${e}-${i}`}
                  type="button"
                  onClick={() => handleSelect(e)}
                  className="text-[20px] leading-none w-9 h-9 flex items-center justify-center rounded hover:bg-emerald-50 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  data-testid={`wa-emoji-${e}`}
                >
                  {e}
                </button>
              ))}
            </div>
            <div className="px-2 py-1 text-[10px] text-neutral-500 border-t bg-neutral-50 flex items-center justify-between">
              <span>Compatibles con WhatsApp · Unicode 6-8</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-emerald-700 hover:underline"
              >
                Cerrar
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
